import {
  Box,
  Heading,
  Text,
  Badge,
  HStack,
  VStack,
  useColorModeValue,
  Button,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { Rock, User } from '../../types';
import { Calendar, User as UserIcon, CheckCircle, XCircle } from 'lucide-react';
import { TriageModal } from './TriageModal';
import { Card } from '../../../../components/Card';
import { Timestamp } from 'firebase/firestore';
import { useHistory } from 'react-router-dom';

interface HistoricalRockCardProps {
  rock: Rock;
  users: User[];
  onTriage?: (rockId: string) => void;
}

/**
 * Historical Rock Card Component
 * Displays a single historical rock (achieved or dropped) with details
 * Optimized for screen real estate
 */
export function HistoricalRockCard({ rock, users, onTriage }: HistoricalRockCardProps) {
  const history = useHistory();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking the Reactivate button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    history.push(`/eos/historical-rocks/${rock.id}`);
  };

  const getUserName = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user?.name || 'Unknown';
  };

  const formatDate = (date: Date | string | Timestamp | undefined) => {
    if (!date) return 'N/A';
    if (date instanceof Timestamp) {
      return date.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const isAchieved = rock.isAchieved;
  const isDropped = rock.isDropped;
  const statusColor = isAchieved ? 'green' : 'red';
  const statusIcon = isAchieved ? CheckCircle : XCircle;

  return (
    <>
      <Card 
        borderWidth="1px" 
        borderColor={borderColor} 
        shadow="sm" 
        p={2}
        cursor="pointer"
        onClick={handleCardClick}
        _hover={{ bg: hoverBg, borderColor: 'blue.300' }}
        transition="all 0.2s"
      >
        <VStack align="stretch" spacing={1}>
          {/* Header Row: Status, Title, Owner */}
          <HStack justify="space-between" align="start" spacing={2}>
            <HStack flex={1} align="start" spacing={2}>
              <Icon as={statusIcon} w={4} h={4} color={`${statusColor}.500`} mt={1} />
              <Box>
                <Heading size="sm" color={headingColor} noOfLines={1} title={rock.rock}>
                  {rock.rock}
                </Heading>
                <Text fontSize="xs" color={textColor} noOfLines={1} title={rock.measurable}>
                  {rock.measurable}
                </Text>
              </Box>
            </HStack>
            
            <VStack align="end" spacing={0}>
              <HStack spacing={1}>
                <Icon as={UserIcon} w={3} h={3} color={textColor} />
                <Text fontSize="xs" color={textColor} fontWeight="medium">
                  {getUserName(rock.ownerId)}
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Icon as={Calendar} w={3} h={3} color={textColor} />
                <Text fontSize="xs" color={textColor}>
                  {formatDate(isAchieved ? rock.achievedAt : rock.droppedAt)}
                </Text>
              </HStack>
            </VStack>
          </HStack>

          {/* Footer Row: Progress & Actions */}
          <HStack justify="space-between" pt={1}>
            {rock.progress !== undefined && (
              <Badge variant="subtle" colorScheme={rock.progress >= 100 ? 'green' : 'gray'} fontSize="xs">
                {rock.progress}% Complete
              </Badge>
            )}
            
            {isDropped && onTriage && (
              <Button
                size="xs"
                colorScheme="blue"
                variant="ghost"
                onClick={onOpen}
                h={6}
              >
                Reactivate
              </Button>
            )}
          </HStack>
        </VStack>
      </Card>

      {isDropped && (
        <TriageModal
          isOpen={isOpen}
          onClose={onClose}
          rock={rock}
          onTriage={() => {
            onTriage?.(rock.id);
            onClose();
          }}
        />
      )}
    </>
  );
}
